export function nonConstantCoefficients(analysis) {
  if (!analysis || !analysis.coefficients) return [];
  return analysis.coefficients.filter(function(c) {
    return c.variable !== "(Constante)";
  });
}

export function betaProportions(analysis) {
  const rows = nonConstantCoefficients(analysis);
  const totalAbsBeta = rows.reduce(function(sum, c) {
    return sum + Math.abs(c.beta || 0);
  }, 0);
  return rows.map(function(c) {
    const absBeta = Math.abs(c.beta || 0);
    return {
      variable: c.variable,
      beta: c.beta,
      proportion: totalAbsBeta > 0 ? absBeta / totalAbsBeta : 0,
    };
  });
}

export const MIN_NODE_SIZE = 18;
export const MAX_NODE_SIZE = 74;
export const NODE_SIZE_PCT_CAP = 42;

export function nodeSize(pct) {
  const capped = Math.min(pct || 0, NODE_SIZE_PCT_CAP);
  return MIN_NODE_SIZE + Math.sqrt(capped / NODE_SIZE_PCT_CAP) * (MAX_NODE_SIZE - MIN_NODE_SIZE);
}

// Radio fijo para nodos "Sin modelo" (dominio o hub) — no_model no trae
// r_squared_adj, así que nodeSize no aplica; fijo y lo bastante grande para
// que quepan nombres de dominio largos ("Extralaboral"/"Intralaboral") +
// "Sin modelo" sin desbordar el círculo. Único valor compartido por
// DomainNode/IntralaboralNode (el círculo en sí) y RelationsNetwork (la
// distancia de órbita al hub) para que nunca queden desincronizados.
export const NOMODEL_RADIUS = 54;

export function roundRSquaredAdj(analysis) {
  if (!analysis || !analysis.summary || !analysis.summary.length) return null;
  const last = analysis.summary[analysis.summary.length - 1];
  return Math.round(last.r_squared_adj * 100);
}

export function confidenceLabel(pValue) {
  if (pValue == null) return null;
  if (pValue < 0.01) return "alta confianza";
  if (pValue < 0.05) return "confianza moderada";
  return "confianza baja";
}

export const CONDITION_LABELS = {
  CLIDER: "Liderazgo",
  RELSOC: "Relaciones sociales",
  RETRO: "Retroalimentación",
  CLROL: "Claridad de rol",
  CAPAC: "Capacitación",
  PARTIC: "Participación y cambio",
  OPORT: "Habilidades y conocimientos",
  CONTR: "Control y autonomía",
  DEMFIS: "Demandas ambientales y físicas",
  DEMEMO: "Demandas emocionales",
  DEMCUANTI: "Demandas cuantitativas",
  INFTRA: "Influencia del trabajo sobre el entorno extralaboral",
  CMENTAL: "Demandas de carga mental",
  JORNADA: "Demandas de la jornada de trabajo",
  RECOMP: "Recompensas del trabajo",
  RECONO: "Reconocimiento",
  TOTALEXTRA: "Total FRP extralaborales",
  INDICOIND: "Indicador de condiciones individuales",
};

export function getConditionLabel(code) {
  return CONDITION_LABELS[code] || code;
}

export const CONDITION_GROUPS = {
  CLIDER: "Liderazgo y relaciones sociales",
  RELSOC: "Liderazgo y relaciones sociales",
  RETRO: "Liderazgo y relaciones sociales",
  CLROL: "Control sobre el trabajo",
  CAPAC: "Control sobre el trabajo",
  PARTIC: "Control sobre el trabajo",
  OPORT: "Control sobre el trabajo",
  CONTR: "Control sobre el trabajo",
  DEMFIS: "Demandas del trabajo",
  DEMEMO: "Demandas del trabajo",
  DEMCUANTI: "Demandas del trabajo",
  INFTRA: "Demandas del trabajo",
  CMENTAL: "Demandas del trabajo",
  JORNADA: "Demandas del trabajo",
  RECOMP: "Recompensas",
  RECONO: "Recompensas",
};

export const EFFECT_KEYS = [
  { apiKey: "Engagement", label: "Engagement" },
  { apiKey: "Estres", label: "Estrés" },
  { apiKey: "Burnout", label: "Burnout" },
];

// design.md §4.2 — orden de los 4 sub-dominios de Intralaboral tal como
// quedó validado en el Artifact (Liderazgo → Control → Demandas →
// Recompensas), para que los nodos del mismo grupo/color queden agrupados
// en vez de mezclados en el orden en que aparecen en los 3 análisis.
const GROUP_ORDER = {
  "Liderazgo y relaciones sociales": 0,
  "Control sobre el trabajo": 1,
  "Demandas del trabajo": 2,
  Recompensas: 3,
};

export function buildSankeyData(results) {
  function pushIf(res, fn) {
    if (!res || res.no_model) return;
    fn(res);
  }

  const leftCodes = {};
  EFFECT_KEYS.forEach(function(e) {
    pushIf(results[e.apiKey], function(res) {
      nonConstantCoefficients(res).forEach(function(c) {
        leftCodes[c.variable] = true;
      });
    });
  });
  const leftNodes = Object.keys(leftCodes)
    .map(function(code) {
      return { code: code, label: getConditionLabel(code), group: CONDITION_GROUPS[code] };
    })
    .sort(function(a, b) {
      return GROUP_ORDER[a.group] - GROUP_ORDER[b.group];
    });

  const rightNodes = EFFECT_KEYS.map(function(e) {
    const res = results[e.apiKey];
    return { effect: e.apiKey, label: e.label, noModel: !res || res.no_model === true };
  });

  const links = [];
  EFFECT_KEYS.forEach(function(e) {
    pushIf(results[e.apiKey], function(res) {
      nonConstantCoefficients(res).forEach(function(c) {
        links.push({
          source: c.variable,
          target: e.apiKey,
          value: Math.abs(c.beta),
          beta: c.beta,
          p_value: c.p_value,
        });
      });
    });
  });

  return { leftNodes: leftNodes, rightNodes: rightNodes, links: links };
}



// WEBPACK FOOTER //
// ./src/components/frpm/components/variables-prioritarias/regression-helpers.js